import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { getHamsKey } from '../../../services/product-to-hams-key';
import { ModalEntryPoint } from '../shared/modal-entry-point';
import { DIALOG_CONFIG } from './dialog-config';
import { type PlanSelectionPageModalType } from './plan-selection-page-modal';
import { entryPoint } from './planSelectionPageModal.entrypoint';

const TWC_MODAL_CONFIG_DEFAULT = {
	width: '1116px',
	height: '733px',
};

const REVERSE_TRIAL_MODAL_CONFIG = {
	width: '968px',
	height: '740px',
};

/**
 * The contact sales footer is laid out underneath the plan selection iframe, so the
 * dialog grows by the footer's height — a single line of body text plus its
 * `space.300` bottom padding — to keep the iframe at its full height. Revisit this if
 * `contactSalesFooter` in `plan-selection-page-modal.tsx` changes its padding.
 */
export const REVERSE_TRIAL_CONTACT_SALES_MODAL_CONFIG = {
	width: '968px',
	height: '784px',
};

const getModalConfig = (
	type: PlanSelectionPageModalType,
	isShowingContactSalesMessage: boolean,
) => {
	if (type === 'reverse-trial') {
		return isShowingContactSalesMessage
			? REVERSE_TRIAL_CONTACT_SALES_MODAL_CONFIG
			: REVERSE_TRIAL_MODAL_CONFIG;
	}
	return TWC_MODAL_CONFIG_DEFAULT;
};

export function PlanSelectionPageModal({
	type,
}: {
	type: PlanSelectionPageModalType;
}): React.JSX.Element {
	const { cloudId, product, userId } = useEditionAwareness();
	const { formatMessage } = useIntl();

	const shouldCheckContactSalesEligibility =
		type === 'reverse-trial' && Boolean(userId) && fg('twcg-1533-pls-contact-path-disco-gate');

	// The cohort that decides whether the contact sales footer renders is only known
	// inside the dialog, so it reports back up here. The ref holds the latched value for
	// the `modalProps` getters below; the state exists purely to re-render the open
	// dialog so those getters are read again.
	const isShowingContactSalesMessageRef = useRef(false);
	const [, setIsShowingContactSalesMessage] = useState(false);

	const onContactSalesMessageVisibilityChange = useCallback((isVisible: boolean) => {
		isShowingContactSalesMessageRef.current = isVisible;
		setIsShowingContactSalesMessage(isVisible);
	}, []);

	// Reopening the dialog should start at the base height, until the cohort resolves again
	const onClose = useCallback(
		() => onContactSalesMessageVisibilityChange(false),
		[onContactSalesMessageVisibilityChange],
	);

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			hamsProductKey: getHamsKey(product),
			shouldCheckContactSalesEligibility,
			userId,
		}),
		[cloudId, product, shouldCheckContactSalesEligibility, userId],
	);

	const entryPointProps = useMemo(
		() => ({ onClose, onContactSalesMessageVisibilityChange, type }),
		[onClose, onContactSalesMessageVisibilityChange, type],
	);

	// `ModalTrigger` captures this object when the dialog opens, so it has to stay stable
	// while the contact sales cohort resolves. The latched visibility is read back through
	// getters instead.
	const modalProps = useMemo(() => {
		const label = formatMessage({
			id: '@atlassian/edition-awareness.dialog.plan-selection-page.title',
			defaultMessage: 'Plan selection',
			description:
				'Accessible name for the plan selection dialog, announced to assistive technology users.',
		});
		const config = () =>
			getModalConfig(
				type,
				shouldCheckContactSalesEligibility && isShowingContactSalesMessageRef.current,
			);

		return {
			get width() {
				return config().width;
			},
			get height() {
				return config().height;
			},
			shouldScrollInViewport: false,
			shouldCloseOnEscape: true,
			label,
		};
	}, [type, shouldCheckContactSalesEligibility, formatMessage]);

	return (
		<ModalEntryPoint
			entryPointProps={entryPointProps}
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			modalProps={modalProps}
			dialogConfig={DIALOG_CONFIG}
		/>
	);
}
